import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, take, tap } from 'rxjs/operators';

import {
  ClientAppSettingsDto as WalletClientAppSettingsDto,
  CountryDto,
  SettingsClient as WalletSettingsClient,
  StoreCategoryDto,
  UserCurrencySettingsDto,
  UsersClient,
  WALLET_API_URL,
} from '../../../api/wallet-api.generated';
import { ConfigurationService } from '../configuration/configuration.service';
import { ApplicationFeatures } from '../../enums/enums';

@Injectable({
  providedIn: 'root',
})
export class AppSettingsService {
  public static agreementsUrl = '';
  public static businessTermsOfServiceFile = '';
  public static consumerPrivacyPolicyFile = '';
  public static consumerTermsOfServiceFile = '';
  public static publicImagesLocation = '';
  public static walletApiUrl = '';
  public static authServiceUrl = '';
  public static settingsLoaded = false;
  public static googlePlacesApiKey = '';
  public static blocktradeConfig = '';
  public static blocktradeDomain: string;
  public static affiliateTermsOfServiceFile = '';
  public static currencyRateSource: string;
  public static siteKey = '';

  public static simplexUrl = '';
  public static swipeluxUrl = '';
  public static supportedFiatCurrencyCodes: string[];

  public static disabledTokens: string[];

  // DO NOT set to empty string, otherwise Stripe throws errors in unit tests!
  public static stripePublicKey =
    'pk_test_51Lei0ZC1C4ZH9z0Y3kwRIv9boa5HGEBoiUD8CqNFpMIL9yOy84Bn6QFp98hRnzKaki2KFP6tybI0otjGQKSNevD200f0Tz7h8K';

  public static eActivityContractAddress: string;
  public static disabledFeatures: ApplicationFeatures[];
  public settings$: Observable<WalletClientAppSettingsDto>;

  private userCurrencySettings: UserCurrencySettingsDto;
  private countires: CountryDto[];
  private storeCategories: StoreCategoryDto[];

  private currencysettings: BehaviorSubject<UserCurrencySettingsDto> = new BehaviorSubject(null);

  constructor(
    private readonly usersClient: UsersClient,
    private walletSettingsClient: WalletSettingsClient,
    @Inject(WALLET_API_URL) walletApiUrl: string
  ) {
    AppSettingsService.walletApiUrl = walletApiUrl;
  }

  get currencySettings$(): Observable<UserCurrencySettingsDto> {
    return this.currencysettings.pipe();
  }

  set currencySettings(value: UserCurrencySettingsDto) {
    this.currencysettings.next(value);
  }

  public static loadSettings$(walletSettingsClient: WalletSettingsClient, configurationService: ConfigurationService) {
    return walletSettingsClient.clientAppSettings2().pipe(
      tap((cfg) => {
        this.agreementsUrl = cfg.agreementsUrl;
        this.businessTermsOfServiceFile = cfg.businessTermsOfServiceFile;
        this.consumerPrivacyPolicyFile = cfg.consumerPrivacyPolicyFile;
        this.consumerTermsOfServiceFile = cfg.consumerTermsOfServiceFile;
        this.publicImagesLocation = cfg.publicImagesLocation;
        this.authServiceUrl = cfg.authIssuerUrl;
        this.googlePlacesApiKey = cfg.googlePlacesPublicKey;
        this.stripePublicKey = cfg.stripePublicKey;
        this.settingsLoaded = true;
        this.eActivityContractAddress = cfg.eActivityContractAddress;
        this.disabledFeatures = cfg.disabledFeatures as ApplicationFeatures[];
        this.blocktradeConfig = cfg.blocktradeConfig;
        configurationService.walletApiUrl = this.walletApiUrl;
        configurationService.disabledFeaturesList = cfg.disabledFeatures as ApplicationFeatures[];
        this.blocktradeDomain = cfg.blocktradeDomain;
        this.affiliateTermsOfServiceFile = cfg.affiliateTermsOfServiceFile;
        this.currencyRateSource = cfg.currencyRateSource;
        this.simplexUrl = cfg.simplexUrl;
        this.swipeluxUrl = cfg.swipeluxUrl;
        this.supportedFiatCurrencyCodes = cfg.supportedFiatCurrencyCodes;
        this.disabledTokens = cfg.disabledTokens;
      })
    );
  }

  getCountries$(): Observable<CountryDto[]> {
    if (this.countires) {
      return of(this.countires);
    } else {
      return this.walletSettingsClient
        .getCountries(undefined)
        .pipe(tap((data: CountryDto[]) => (this.countires = data)));
    }
  }

  getStoreCategories$(): Observable<StoreCategoryDto[]> {
    if (this.storeCategories) {
      return of(this.storeCategories);
    } else {
      return this.walletSettingsClient
        .getStoreCategories()
        .pipe(tap((data: StoreCategoryDto[]) => (this.storeCategories = data)));
    }
  }

  getCurrencySettings(): Observable<UserCurrencySettingsDto> {
    return this.usersClient.getCurrencySettings().pipe(
      tap((data: UserCurrencySettingsDto) => (this.currencySettings = data)),
      take(1),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((e) => throwError(e))
    );
  }
}
